/**
 * @module root/web
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
import { Routes } from '@angular/router';
import { APP_ROUTES } from '../root.routes';
import { SHARED_CONSTANTS } from '@root/shared/constants/shared-constants.constants';
import { WebHomeComponent } from '@experience/landing/web/components/web-home/web-home.component';
import { CustomerProfileComponent } from './../../support/customer-login/components/customer-profile.component';
import { LineLandingComponent } from '@experience/landing/common/components/line-landing/line-landing.component';
import { ZipCodeRequiredGuardService } from '@experience/shop/services/zip-code-required-guard.service';
import { MarketingComponent } from '@root/shared/components/marketing/marketing.component';
import { LoadGuardService } from '@support/shared/widgets/services/load-guard.service';
import { LoadAuthenticationGuardService } from '@support/shared/widgets/services/load-authentication-guard.service';
import { ReceiptRetrievalFeatureRouteGuard } from '@core/guards/receipt-retrieval-feature-route.guard';

/**
 * Web only routes
 */
export const WEB_ROUTES: Routes = [
  ...APP_ROUTES,
  {
    path: '',
    redirectTo: '/auth',
    pathMatch: 'full',
  },
  {
    path: 'home',
    component: WebHomeComponent,
    data: {
      pdlPageComponentId: 'WebHomeComponent',
      preload: true,
      delay: false,
    },
  },
  {
    path: 'auth',
    component: CustomerProfileComponent,
    data: { preload: true, delay: true },
  },
  {
    path: 'bring-your-own-device',
    canActivate: [ZipCodeRequiredGuardService],
    loadChildren: () => import('@experience/shop/byod/byod.module').then((m) => m.ByodModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'my-profile',
    loadChildren: () =>
      import('@experience/modules/manage-profile/manage-profile.module').then(
        (m) => m.ManageProfileModule
      ),
    canActivate: [LoadAuthenticationGuardService],
    data: { preload: true, delay: true },
  },
  {
    path: 'usage-details',
    canLoad: [LoadAuthenticationGuardService],
    loadChildren: () =>
      import('@experience/usage-details/usage-details.module').then((m) => m.UsageDetailsModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'ebb',
    canActivate: [LoadAuthenticationGuardService],
    loadChildren: () => import('@experience/ebb/ebb.module').then((m) => m.EbbModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'customer-activity-log',
    canLoad: [LoadAuthenticationGuardService],
    loadChildren: () =>
      import('@experience/customer-activity-log/customer-activity-log.module').then(
        (m) => m.CustomerActivityLogModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'billing-landing',
    loadChildren: () =>
      import('@experience/billing-landing/billing-landing.module').then(
        (m) => m.BillingLandingModule
      ),
    canLoad: [LoadAuthenticationGuardService],
    data: { preload: true, delay: true },
  },
  {
    path: 'billing-details',
    loadChildren: () =>
      import('@experience/billing-details/billing-details.module').then(
        (m) => m.BillingDetailsModule
      ),
    canLoad: [LoadAuthenticationGuardService],
    data: { preload: true, delay: true },
  },
  {
    path: 'support-landing',
    canLoad: [LoadAuthenticationGuardService],
    loadChildren: () =>
      import('@experience/support-landing/support-landing.module').then(
        (m) => m.SupportLandingModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'line-landing',
    component: LineLandingComponent,
    canActivate: [LoadAuthenticationGuardService],
    data: { pdlPageComponentId: 'LineLandingComponent', preload: true, delay: true },
  },
  {
    path: 'special-instructions',
    /* tslint:disable-next-line:max-line-length */
    loadChildren: () =>
      import(
        '@experience/landing/common/modules/special-instructions/special-instructions.module'
      ).then((m) => m.SpecialInstructionsModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'coverage-map',
    loadChildren: () =>
      import('@experience/coverage-map/coverage-map.module').then((m) => m.CoverageMapModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'connect/coverage-map',
    loadChildren: () =>
      import('@experience/coverage-map/coverage-map.module').then((m) => m.CoverageMapModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'view-document',
    canActivate: [ReceiptRetrievalFeatureRouteGuard],
    loadChildren: () =>
      import('@experience/receipt-retrieval/receipt-retrieval.module').then(
        (m) => m.ReceiptRetrievalModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'secure-view-document',
    canActivate: [ReceiptRetrievalFeatureRouteGuard, LoadAuthenticationGuardService],
    loadChildren: () =>
      import('@experience/receipt-retrieval/receipt-retrieval.module').then(
        (m) => m.ReceiptRetrievalModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'browse-phones',
    canActivate: [ZipCodeRequiredGuardService],
    loadChildren: () =>
      import('@experience/shop/hardgoods/hardgoods.module').then((m) => m.HardgoodsModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'upgrade-devices',
    canActivate: [ZipCodeRequiredGuardService],
    loadChildren: () =>
      import('@experience/shop/hardgoods/hardgoods.module').then((m) => m.HardgoodsModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'browse-tablets',
    canActivate: [ZipCodeRequiredGuardService],
    loadChildren: () =>
      import('@experience/shop/hardgoods/hardgoods.module').then((m) => m.HardgoodsModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'browse-accessories',
    canActivate: [ZipCodeRequiredGuardService],
    loadChildren: () =>
      import('@experience/shop/hardgoods/hardgoods.module').then((m) => m.HardgoodsModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'browse-plans-phone',
    canActivate: [ZipCodeRequiredGuardService],
    loadChildren: () =>
      import('@experience/shop/softgoods/softgoods.module').then((m) => m.SoftgoodsModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'browse-plans-tablet',
    canActivate: [ZipCodeRequiredGuardService],
    loadChildren: () =>
      import('@experience/shop/softgoods/softgoods.module').then((m) => m.SoftgoodsModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'product-details',
    canActivate: [ZipCodeRequiredGuardService],
    loadChildren: () =>
      import('@experience/shop/product-details/product-details.module').then(
        (m) => m.ProductDetailsModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'cart',
    loadChildren: () =>
      import('@experience/shop/cart/online-cart/online-cart.module').then(
        (m) => m.OnlineCartModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'browse-services',
    loadChildren: () =>
      import('@experience/shop/browse-services/browse-services.module').then(
        (m) => m.BrowseServicesModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'autopay',
    canLoad: [LoadAuthenticationGuardService],
    loadChildren: () =>
      import('@experience/manage-autopay/manage-autopay.module').then((m) => m.ManageAutopayModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'plan-detail/:selectedPlanId',
    canActivate: [ZipCodeRequiredGuardService],
    loadChildren: () =>
      import('@experience/shop/plan-details/plan-details.module').then((m) => m.PlanDetailsModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'refill-account',
    canLoad: [LoadAuthenticationGuardService],
    loadChildren: () =>
      import('@experience/refill-account/refill-account.module').then((m) => m.RefillAccountModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'order-summary',
    loadChildren: () =>
      import('@experience/order-summary/order-summary.module').then((m) => m.OrderSummaryModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'direct-to-account',
    loadChildren: () =>
      import('@experience/directto-account/directto-account.module').then(
        (m) => m.DirecttoAccountModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'imei-check',
    loadChildren: () =>
      import('@experience/imei-check/imei-check.module').then((m) => m.ImeiCheckModule),
    canLoad: [LoadGuardService],
    data: { preload: true, delay: true },
  },
  {
    path: 'unlock-my-device',
    loadChildren: () =>
      import('@experience/unlock-my-device/unlock-my-device.module').then(
        (m) => m.UnlockMyDeviceModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'search',
    loadChildren: () =>
      import('@experience/search-results/search-results.module').then((m) => m.SearchResultsModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'null',
    loadChildren: () =>
      import('@experience/search-results/search-results.module').then((m) => m.SearchResultsModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'lost-stolen-device',
    canLoad: [LoadAuthenticationGuardService],
    loadChildren: () =>
      import('@experience/report-lost-stolen/report-lost-stolen.module').then(
        (m) => m.ReportLostStolenModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'access-puk',
    canLoad: [LoadAuthenticationGuardService],
    loadChildren: () => import('@experience/access-puk/puk.module').then((m) => m.AccessPUKModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'one-time-payment',
    /* tslint:disable-next-line:max-line-length */
    loadChildren: () =>
      import(
        '@experience/one-time-payment/modules/online-one-time-payment/one-time-payment-web/one-time-payment-web.module'
      ).then((m) => m.OneTimePaymentWebModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'change-msisdn',
    canLoad: [LoadAuthenticationGuardService],
    loadChildren: () =>
      import('@experience/change-msisdn/change-msisdn.module').then((m) => m.ChangeMsisdnModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'contact-us',
    loadChildren: () =>
      import('@experience/contact-us/contact-us.module').then((m) => m.ContactUsModule),
    data: { preload: true, delay: true },
  },
  {
    path: 'change-plans-services',
    canActivate: [LoadAuthenticationGuardService],
    loadChildren: () =>
      import('@experience/change-plan-services/change-plan-services.module').then(
        (m) => m.ChangePlanServicesModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: SHARED_CONSTANTS.ERROR_404_ROUTE,
    loadChildren: () => import('@experience/error-pages/error.module').then((m) => m.ErrorModule),
  },
  {
    path: 'order-confirmation',
    loadChildren: () =>
      import('@experience/shop/order-confirmation/order-confirmation.module').then(
        (m) => m.OrderConfirmationModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'validate-msisdn',
    loadChildren: () =>
      import('@experience/directto-account/directto-account.module').then(
        (m) => m.DirecttoAccountModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'verify-phone-number',
    loadChildren: () =>
      import('@experience/directto-account/directto-account.module').then(
        (m) => m.DirecttoAccountModule
      ),
    data: { preload: true, delay: true },
  },
  {
    path: 'marketing',
    children: [
      {
        path: '**',
        component: MarketingComponent,
      },
    ],
    data: { preload: true, delay: false },
  },
  // Logic changed to check for marketing content and then redirect to error if not found
  {
    path: '**',
    component: MarketingComponent,
  },
];
